(ns bp-clients.tv-app.common.components.player
  (:require  ["react" :refer [createRef useEffect]]
             [bp-clients.tv-app.api.events :refer [hermes-url-endpoint]]
             [bp-clients.tv-app.api.graphql.license :refer [license-query]]
             [bp-clients.tv-app.common.components.player-fatal-error :refer [player-fatal-error]]
             [bp-clients.tv-app.common.components.thumb :as thumb-components]
             [bp-clients.tv-app.nav.views.loading :refer [loading]]
             [bp-clients.tv-app.utils.keycodes :as keycodes]
             [bp-clients.tv-app.utils.logger :as logger]
             [bp-clients.tv-app.utils.os :as os-system]
             [bp-clients.tv-app.utils.utils :refer [video-duration cloudinary-image] :as utils]
             [bp-libs.design-system.components.buttons :refer [Button]]
             [bp-libs.design-system.components.carousel :refer [carousel]]
             [bp-libs.design-system.components.icons :refer [arrow-left replay next-media pause play check]]
             [bp-libs.design-system.components.progress :refer [continue-watching-bar]]
             [clojure.string :as str]
             [goog.events :as events]
             [goog.object]
             [re-frame.core :as rf]
             [reagent.core :as r]
             [shaka-player])
  (:import [goog.events EventType]))

(def ^:private resolution-test-users
  #{"princesa@brasilparalelo.com.br"
    "matheus.turkiewicz+1@brasilparalelo.com.br"})

;; install all shaka polyfill. This is required
(-> shaka-player .-polyfill .installAll)

(when (= "dev" logger/env)
  (some-> shaka-player .-log (.setLevel (-> shaka-player .-log .-Level .-V2))))

(def shaka-config
  {:abr                   {:defaultBandwidthEstimate 5000000
                          ;; Downgrade if more than 95% of estimate is used
                          ;; increase to be less conservative
                           :bandwidthDowngradeTarget 0.95
                          ;; upgrade if it would use less than 95% of the estimate
                          ;; decrease to be more conservative
                           :bandwidthUpgradeTarget   0.95
                           :switchInterval           5
                          ;; https://shaka-player-demo.appspot.com/docs/api/shaka.extern.html#.AdvancedAbrConfiguration
                          ;; minBytes: Minimum number of bytes, under which samples are discarded.
                          ;; Our models do not include latency information, so connection startup
                          ;; time (time to first byte) is considered part of the download time.
                          ;; Because of this, we should ignore very small downloads which would
                          ;; cause our estimate to be too low.
                          ;; Problem reported in:
                          ;; https://github.com/shaka-project/shaka-player/issues/3422
                          ;; We were suferring from the same issue. Set up minBytes to 64KB
                          ;; to ignore audio chunks, that makes badwidth estimation decrease.
                           :advanced                 {:minBytes 64000}}
   :preferForcedSubs       true
   :preferredTextRole     "forced"
   :preferredTextLanguage "pt-BR"
   :manifest              {:retryParameters {:maxAttempts 5}}
   :streaming             {:bufferBehind    20
                           :bufferingGoal   40
                           :retryParameters {:maxAttempts 5}
                           :stallEnabled    false}
   :drm                   {:servers  {"com.widevine.alpha" hermes-url-endpoint}
                           :advanced {"com.widevine.alpha"
                                      {:videoRobustness "SW_SECURE_CRYPTO"
                                       :audioRobustness "SW_SECURE_CRYPTO"}}}})

(def ^:private fancy-subtitles? true)

(defn- license-request-interceptor
  [config request]
  (let [token        (:token config)
        challenge    (shaka-player/util.Uint8ArrayUtils.toStandardBase64 (.-body request))
        query-config (-> config
                         (dissoc :token)
                         (assoc :challenge challenge))]
    (when (empty? token)
      ;; TODO test this flow
      (logger/js-error (js/Error. "DRM token not SET")))
    (goog.object/set (.-headers request) "content-type" "application/json")
    (goog.object/set (.-headers request) "Authorization" token)
    (set! (.-body request) (js/JSON.stringify (clj->js (license-query query-config))))))

(defn- license-response-interceptor [response]
  (set! (.-data response)
        (-> response
            .-data
            shaka-player/util.StringUtils.fromUTF8
            js/JSON.parse
            .-data.drm-license-v-2.license
            shaka-player/util.Uint8ArrayUtils.fromBase64
            .-buffer)))

(defn- config-player
  [shaka refs config]
  (.configure shaka
              (clj->js (cond-> (merge shaka-config (select-keys config [:playRangeStart]))
                         fancy-subtitles?
                         (assoc :textDisplayFactory
                                (fn [] (shaka-player/text.UITextDisplayer.
                                        (:video refs)
                                        (:container refs)))))))

  (when (os-system/webos?)
    (.deviceInfo js/webOS
                 #(.configure shaka
                              #js {"abr" #js{"restrictToScreenSize" true
                                             "restrictions" #js{"maxHeight" (.-screenHeight %)}}})))

  (doto (.getNetworkingEngine shaka)
    (.clearAllRequestFilters)
    (.registerRequestFilter
     (fn [type request _context]
       (when (= type shaka-player/net.NetworkingEngine.RequestType.LICENSE)
         (license-request-interceptor config request))))

    (.clearAllResponseFilters)
    (.registerResponseFilter
     (fn [type response]
       (when (= type shaka-player/net.NetworkingEngine.RequestType.LICENSE)
         (license-response-interceptor response))))))

(def ^:private ignored-shaka-errors
  #{(-> shaka-player/util .-Error .-Code .-LOAD_INTERRUPTED)})

(defn- fatal-error-data [error-code]
  {:initial-selected-group :recommend
   :initial-selected-option :restart
   :group-items {:recommend {:title "Recomendado"
                             :menu-items {:restart-router {:title     "Reinicie o roteador"
                                                           :details   {:error-name "Não é possível reproduzir a mídia"
                                                                       :error-instruction-title "Reinicie o roteador"
                                                                       :error-code (str "reproduction-" error-code)
                                                                       :steps ["1. Desligue o aparelho e depois tire o roteador da tomada."
                                                                               "2. Aguarde 30 segundos e reconecte o roteador."
                                                                               "3. Aguarde 1 minuto e ligue a TV."
                                                                               "4. Tente acessar a Brasil Paralelo novamente."]}}
                                          :restart-device {:title   "Reinicie o aparelho"
                                                           :details {:error-name "Não é possível reproduzir a mídia"
                                                                     :error-instruction-title "Reinicie o aparelho"
                                                                     :error-code (str "reproduction-" error-code)
                                                                     :steps ["1. Desligue a TV e depois retire o aparelho da tomada por 3 minutos."
                                                                             "2. Reconecte a TV na tomada e ligue o aparelho."
                                                                             "3. Tente acessar a Brasil Paralelo novamente."]}}
                                          :update-system  {:title   "Verificar atualização do sistema"
                                                           :details {:error-name "Não é possível reproduzir a mídia"
                                                                     :error-instruction-title "Verificar atualização do sistema"
                                                                     :error-code (str "reproduction-" error-code)
                                                                     :steps ["Esse problema pode indicar que seu aparelho precisa ser atualizado. Para descobrir se essa é a causa do erro, siga as etapas abaixo:"
                                                                             "1. No menu da sua TV, selecione Configurações."
                                                                             "2. Selecione Suporte > Atualizações de software."
                                                                             "3. Selecione Atualizar software."
                                                                             "4. Quando a atualização terminar, tente assistir à Brasil Paralelo de novo."]}}}}}})

(comment

  (-> shaka-player/util .-Error .-Severity .-RECOVERABLE)
  (-> shaka-player/util .-Error .-Severity .-CRITICAL)

  nil)

(defn- load-and-play
  ([shaka-instance url fatal-error? error-data]
   (load-and-play shaka-instance url fatal-error? error-data nil))
  ([shaka-instance url fatal-error? error-data callbackfn]
   (when url
     (-> shaka-instance
         (.load url)
         (.then #(when callbackfn (callbackfn)))
         (.catch (fn [error]
                   (when (= (-> error .-severity) (-> shaka-player/util .-Error .-Severity .-CRITICAL))
                     (reset! fatal-error? true)
                     (reset! error-data (fatal-error-data (.-code error))))
                   (.detach shaka-instance)
                   (when-not (contains? ignored-shaka-errors (.-code error))
                     (logger/js-error error))))))))

(defn- thumb-gen
  [image]
  ^{:key (:id image)}
  [thumb-components/keep-watching {:thumbnail {:image image}}])

(defn- close-track-selection
  [toggle-track-selector event]
  (utils/stop-event-propagation event)
  (toggle-track-selector)
  (.focus js/SpatialNavigation "content"))

(defn- any-forced-subs? [shaka]
  (->> (.getTextLanguagesAndRoles shaka)
       (filter #(= "forced_subtitle" (.-role %)))
       seq
       some?))

(def ^:private language-map
  {:pt-br (new js/Intl.DisplayNames #js ["pt-br"] #js {:type "language"})})

(defn format-language
  [language-code]
  (.of (:pt-br language-map) language-code))

(defn- upper-control-btn [{:keys [react-ref on-click on-key-down label icon tab-index padding]}]
  (let [show-label? (r/atom false)
        show-label  #(reset! show-label? %)]
    (fn []
      [:div {:class       ["relative"
                           "flex flex-col outline-none"
                           "items-center"]
             :on-click    on-click
             :on-key-down on-key-down
             :on-focus     (partial show-label true)
             :on-blur      (partial show-label false)
             :on-mouse-over (partial show-label true)
             :on-mouse-out  (partial show-label false)
             :tab-index   tab-index
             :ref         react-ref}
       [:div {:class ["rounded-full"
                      (when @show-label? "text-gray-900 bg-white")
                      (or padding "p-2")
                      "grow-0"]}
        icon]
       [:span {:class ["absolute"
                       (if @show-label? "visible" "invisible")
                       "top-16 pt-4"
                       "text-center"
                       "w-60"
                       "text-white"
                       "font-bold text-2xl"]}
        label]])))

(defn- track-button
  [{:keys [label on-click on-key-down selected? tab-index]}]
  [Button {:color         "black"
           :extra-classes ["capitalize outline-none"]
           :tab-index     tab-index
           :on-click      on-click
           :on-key-down   on-key-down}
   [:span {:class "px-5 py-1 inline-flex"}
    [:span {:class "mr-2"} label]
    (when selected? [check {:width  30
                            :height 30}])]])

(def ^:private default-seek-speed 10)

(defn- set-current-time
  [video-el new-time]
  (set! (.-currentTime video-el) new-time))

(defn- do-seek!
  [video-el new-time]
  (set-current-time video-el new-time))

(defn on-play-pause-btn-key-down
  [video-el back-btn-el enter-seek-mode event]
  (let [keyCode      (-> event .-keyCode)]
    (cond
      (keycodes/ff? keyCode)
      (do
        (utils/stop-event-propagation event)
        (.pause video-el)
        (enter-seek-mode))

      (keycodes/fr? keyCode)
      (do (utils/stop-event-propagation event)
          (.pause video-el)
          (enter-seek-mode))

      (keycodes/up? keyCode)
      (when-let [back back-btn-el]
        (utils/stop-event-propagation event)
        (.focus back))

      :else  nil)))

(defn- on-progress-bar-click
  [event video-ref media-duration]
  (let [click-position-x     (.-clientX event)
        progress-bounds      (.getBoundingClientRect (.-target event))
        progress-left-bound  (.-left progress-bounds)
        progress-width       (.-width progress-bounds)
        left-width-abs       (- click-position-x progress-left-bound)
        left-width-percent   (/ left-width-abs progress-width)]
    (set-current-time (.-current video-ref) (* media-duration left-width-percent))))

(defn- focus-on-play-pause-button [play-pause-el]
  (when-let [play-pause-btn play-pause-el]
    (js/setTimeout #(.focus play-pause-btn)
                   utils/delay-to-focus)))

(defn- current-text-track-is-forced?
  "Checks if the current text track is a forced subtitle."
  [shaka]
  (let [text-tracks  (.getTextTracks shaka)
        active-track (first (filter #(js->clj (.-active %)) text-tracks))
        forced?      (if active-track
                       (js->clj (.-forced active-track))
                       false)]
    forced?))

(defn- is-standard-track-and-should-be-selected?
  "We have these text track options for the user:
  - Disabled ('desativado' option)
  - Language text tracks (standard tracks)

  The language text tracks should appear as selected
  in the UI when:
  - the player current track language is the same as
  the option"
  [shaka current-subtitle track-language]
  (and current-subtitle
       (not (current-text-track-is-forced? shaka))
       (= track-language current-subtitle)))

(defn- is-disabled-option-and-should-be-selected?
  "We have these text track options for the user:
  - Disabled ('desativado' option)
  - Language text tracks (standard tracks)

  The forced subtitles are hidden from options.

  The disabled option should appear as selected when:
  - There is no subtitle selected OR
  - When there is a forced subtitle selected"
  [shaka current-subtitle track-language]
  (let [current-is-this?   (= current-subtitle track-language)
        current-is-forced? (current-text-track-is-forced? shaka)]
    (and (= "none" track-language)
         (or current-is-forced? current-is-this?))))

(defn- get-media-percent
  [total current]
  (* 100 (/ (or current 0) (or total 1))))

(defn- close-player [state on-close-player-fn]
  (swap! state #(assoc % :closed? true))
  (.detach (:shaka @state))
  (on-close-player-fn))

(defn- controls
  [{:keys [media-sub config-sub events video-ref tab-index state]}]
  (let [{:keys [on-close-player on-next-video on-pause
                toggle-track-selector set-audio-track on-show-player-controls
                set-subtitle-track]} events
        media                (rf/subscribe media-sub)
        show-suggestion?     (r/atom false)
        seek-mode?           (r/atom false)
        config               (rf/subscribe config-sub)
        ref-block            (createRef)
        new-seek             (r/atom 0)
        seek-speed           (r/atom 1)
        last-seek-timestamp  (r/atom nil)
        show-play-btn-label? (r/atom false)
        seek-interval-id     (r/atom nil)]

    (fn []
      (let [current-time         (-> @config :controls :current-time)
            media-duration       (-> @media :drm :duration)
            paused?              (-> @config :controls :paused?)
            audio-tracks         (-> @config :controls :audio-tracks)
            subtitle-tracks      (-> @config :controls :subtitle-tracks)
            all-tracks           (apply merge audio-tracks subtitle-tracks)
            show-others?         (> (count all-tracks) 2)
            track-selector-open? (-> @config :controls :track-selector-open?)
            current-audio        (-> @config :controls :audio-track-selected)
            current-subtitle     (-> @config :controls :subtitle-track-selected)
            main-audio           (first (filter #(= "main" (:role %)) audio-tracks))
            main-language        (:language main-audio)
            media-name           (-> @media :drm :name)
            playlist-name        (or (-> @media :drm :playlist :name) (:subtitle @media))
            back-btn-ref         (createRef)
            from-start-btn-ref   (createRef)
            next-media-btn-ref   (createRef)
            ref-seek             (createRef)
            set-new-seek         #(reset! new-seek (min (max % 0) media-duration))
            play-pause-btn-ref   (createRef)
            set-seek-mode        (fn [enabled?]
                                   (reset! seek-mode? enabled?)
                                   (when enabled?
                                     (.focus (.-current ref-seek))
                                     (set-new-seek current-time)))
            get-dynamic-media-percent #(get-media-percent media-duration (if @seek-mode? @new-seek current-time))]

        ;; TODO we should avoid useEffect hook due performance
        ;; issues. Instead try to use reagent.ratom/atom
        (useEffect
         (fn []
           (when (or (:can-play? @config) (:show-controls? @config))
             (when-let [play-pause (.-current play-pause-btn-ref)]
               (focus-on-play-pause-button play-pause))

             (set-seek-mode false))
           ;; do not require cleanup phase,
           ;; so should return undefined.
           js/undefined)
         (clj->js [(-> @config :show-controls?) (-> @config :can-play?)]))

        (useEffect
         (fn []
           (when-not @seek-mode?
             (when-let [play-pause (.-current play-pause-btn-ref)]
               (focus-on-play-pause-button play-pause)))
           js/undefined)
         (clj->js [@seek-mode?]))

        [:div {:ref   ref-block
               :class ["z-20 relative"
                       (when (or (-> @config :can-play? not)
                                 (-> @config :show-controls? not))
                         "invisible")
                       (when (-> @config :show-controls?)
                         "controls-open")]} ; When set, this class moves up the subtitles via CSS
         [:div {:class ["drop-shadow absolute top-0 z-20 -right-96 w-96 h-screen bg-black transition-all transform ease-in-out duration-150"
                        (when track-selector-open? "-translate-x-full")]}
          [:div {:class "p-10 flex space-y-10 text-white flex-col text-2xl w-full"}
           [:div {:class "mx-8 pt-2"}
            [:div {:class "mb-4 text-4xl"} "Audio"]
            (doall
             (for [{:keys [role language label] :as track} audio-tracks
                   :let                                    [selected? (= current-audio language)
                                                            display-name (or label (format-language language))]]
               ^{:key (str role language label)}
               [:div {:on-click  #(when set-audio-track
                                    (utils/stop-event-propagation %)
                                    (set-audio-track track))
                      :on-key-down #(let [event %
                                          key-code (-> event .-keyCode)]
                                      (cond
                                        (keycodes/left? key-code)
                                        (close-track-selection toggle-track-selector event)

                                        :else nil))
                      :tab-index (when track-selector-open? (+ tab-index 20))
                      :class     ["p-2 last:mb-16 rounded capitalize focus:bg-white focus:text-black flex items-center justify-between outline-none"]}
                display-name (when selected? [check {:width 30 :height 30}])]))]
           [:div {:class "mx-8 pt-2 last:mb-16"}
            [:div {:class "mb-4 text-4xl"} "Legenda"]
            (doall
             (for [{:keys [role language label] :as track} (filter #(not= (:role %) "forced_subtitle")
                                                                   subtitle-tracks)
                   :let                                    [selected? (and (:shaka @state)
                                                                           (or
                                                                            (is-standard-track-and-should-be-selected? (:shaka @state)  current-subtitle language)
                                                                            (is-disabled-option-and-should-be-selected? (:shaka @state) current-subtitle language)))
                                                            display-name (or label (format-language language))]]
               ^{:key (str role language label)}
               [:div {:on-click  #(when set-subtitle-track
                                    (utils/stop-event-propagation %)
                                    (set-subtitle-track track))
                      :on-key-down #(let [event %
                                          key-code (-> event .-keyCode)]
                                      (cond
                                        (keycodes/left? key-code)
                                        (close-track-selection toggle-track-selector event)

                                        :else nil))
                      :tab-index (when track-selector-open? (+ tab-index 20))
                      :class     ["p-2 last:mb-16 rounded capitalize focus:bg-white focus:text-black flex items-center justify-between outline-none"]}
                display-name (when selected? [check {:width  30 :height 30}])]))]]]
         [:div {:class ["controls absolute justify-between flex flex-col overflow-hidden"
                        "w-screen h-screen"
                        "top-0 pt-28 pb-8 px-32"
                        "bg-black/50"
                        "text-gray-50 text-base font-normal leading-7"]}
          [:div {:class "h-20 flex items-center justify-between"}
           [:div {:class ["space-x-14 flex items-center justify-around"
                          (when @seek-mode? "invisible")]}
            [upper-control-btn {:icon        [arrow-left]
                                :tab-index   tab-index
                                :label       "Voltar"
                                :react-ref   back-btn-ref
                                :on-click    #(close-player state on-close-player)
                                :on-key-down #(let [event %
                                                    key-code (-> event .-keyCode)]
                                                (cond
                                                  (keycodes/down? key-code)
                                                  (when-let [play-pause play-pause-btn-ref.current]
                                                    (utils/stop-event-propagation %)
                                                    (.focus play-pause))

                                                  (keycodes/right? key-code)
                                                  (when-let [watch-from-start (.-current from-start-btn-ref)]
                                                    (utils/stop-event-propagation %)
                                                    (.focus watch-from-start))

                                                  :else nil))}]
            [upper-control-btn {:icon      [replay]
                                :tab-index tab-index
                                :react-ref from-start-btn-ref
                                :label     "Assistir do início"
                                :on-click  (fn [event]
                                             (utils/stop-event-propagation event)
                                             (set-current-time video-ref.current 0))
                                :on-key-down #(let [event %
                                                    key-code (-> event .-keyCode)]
                                                (cond
                                                  (keycodes/down? key-code)
                                                  (when-let [play-pause play-pause-btn-ref.current]
                                                    (utils/stop-event-propagation %)
                                                    (.focus play-pause))

                                                  (keycodes/right? key-code)
                                                  (when-let [next-video next-media-btn-ref.current]
                                                    (utils/stop-event-propagation %)
                                                    (.focus next-video))

                                                  (keycodes/left? key-code)
                                                  (when-let [back back-btn-ref.current]
                                                    (utils/stop-event-propagation %)
                                                    (.focus back))

                                                  :else nil))}]
            (when-let [next-media-for-player (get-in @media [:drm :next_media_for_player])]
              [upper-control-btn {:icon        [next-media {:width 31 :height 31}]
                                  :tab-index   tab-index
                                  :react-ref   next-media-btn-ref
                                  :padding     "p-4"
                                  :label       "Ir para próxima mídia"
                                  :on-click    #(on-next-video {:id (:id next-media-for-player)}
                                                               {:slug (get-in next-media-for-player [:playlist :slug])})
                                  :on-key-down #(let [event    %
                                                      key-code (-> event .-keyCode)]
                                                  (cond
                                                    (keycodes/down? key-code)
                                                    (when-let [play-pause play-pause-btn-ref.current]
                                                      (utils/stop-event-propagation %)
                                                      (.focus play-pause))

                                                    (keycodes/left? key-code)
                                                    (when-let [watch-from-start (.-current from-start-btn-ref)]
                                                      (utils/stop-event-propagation %)
                                                      (.focus watch-from-start))

                                                    (keycodes/right? key-code)
                                                    (utils/stop-event-propagation %)

                                                    :else nil))}])]
           [:div {:class ["text-right space-y-2"
                          (when @seek-mode? "invisible")]}
            [:div {:class "font-bold leading-8 text-4xl"} media-name]
            (when (not= media-name playlist-name) [:div {:class "text-xl"} playlist-name])]]
          [:div {:class ["h-52 flex shrink-0 flex-col space-y-4 transition-all transform ease-in-out duration-150 mb-4"
                         (when @show-suggestion? "opacity-0")]}
           [:div {:class "space-x-6 flex items-center"}
            [:div {:class         ["relative"
                                   "flex flex-col"
                                   "items-center space-y-6"]}
             [:div {:tab-index     tab-index
                    :ref           play-pause-btn-ref
                    :on-focus      #(reset! show-play-btn-label? true)
                    :on-mouse-over #(reset! show-play-btn-label? true)
                    :on-blur       #(reset! show-play-btn-label? false)
                    :on-mouse-out  #(reset! show-play-btn-label? false)
                    :on-key-down   (partial on-play-pause-btn-key-down video-ref.current back-btn-ref.current (partial set-seek-mode true))
                    :on-click      (when on-pause #(do
                                                     (utils/stop-event-propagation %)
                                                     (on-pause (clj->js {:type "PLAY/PAUSE" :target video-ref.current}))))
                    :class         ["p-1 rounded-full text-black h-24 bg-black text-white"
                                    "focus:bg-white focus:text-black outline-none transition-[width] duration-500 easy-in-out"
                                    (if @seek-mode? "invisible w-1" "w-24")]}
              (if paused?
                [play {:variant :solid
                       :width   "100%"
                       :height  "100%"}]
                [pause {:width  "100%"
                        :height "100%"}])]
             [:span {:class ["absolute"
                             (if @show-play-btn-label? "visible" "invisible")
                             "top-16 pt-4"
                             "text-center"
                             "w-60"
                             "text-white"
                             "font-bold text-2xl"]}
              (if paused? "Assistir" "Pausar")]]
            [:div {:class "text-white font-bold text-2xl min-w-16 text-left"} (video-duration (if @seek-mode? @new-seek current-time))]
            [:div {:on-click #(on-progress-bar-click % video-ref media-duration)
                   :class "flex-1 items-center range-component outline-none"
                   :tab-index -1 ;; so it can be focused
                   :ref      ref-seek
                   :on-key-up  #(do
                                  (reset! seek-speed 1)
                                  (reset! last-seek-timestamp nil)
                                  (swap! seek-interval-id js/clearInterval))
                   :on-key-down #(let [event                %
                                       keyCode              (-> event .-keyCode)
                                       now                  (.now js/Date)
                                       multi-factor         0.2
                                       forward-factor       1.1
                                       rewind-factor        0.9
                                       trigger-threshold-ms 2000]

                                   (on-show-player-controls true)

                                   (when (nil? @last-seek-timestamp)
                                     (reset! last-seek-timestamp now))

                                   (.pause js/SpatialNavigation)

                                   (cond
                                     (keycodes/fr? keyCode)
                                     (do (swap! seek-interval-id js/clearInterval)
                                         (let [seek-callback (fn []
                                                               (when (>= (- now @last-seek-timestamp) trigger-threshold-ms)
                                                                 (reset! last-seek-timestamp now)
                                                                 (reset! seek-speed (max (- @seek-speed (* multi-factor @seek-speed)) rewind-factor)))
                                                               (set-new-seek (max (* (- @new-seek default-seek-speed) @seek-speed) 0)))]
                                           (seek-callback)
                                           (when (os-system/tizen?)
                                             (reset! seek-interval-id (js/setInterval seek-callback 200)))))

                                     (keycodes/ff? keyCode)
                                     (do (swap! seek-interval-id js/clearInterval)
                                         (let [seek-callback (fn []
                                                               (when (>= (- now @last-seek-timestamp) trigger-threshold-ms)
                                                                 (reset! last-seek-timestamp now)
                                                                 (reset! seek-speed (min (+ @seek-speed (* multi-factor @seek-speed)) forward-factor)))
                                                               (set-new-seek (min (* (+ @new-seek default-seek-speed) @seek-speed) media-duration)))]
                                           (seek-callback)
                                           (when (os-system/tizen?)
                                             (reset! seek-interval-id (js/setInterval seek-callback 200)))))

                                     (keycodes/previous? keyCode)
                                     (do
                                       (utils/stop-event-propagation %)
                                       (.play video-ref.current)
                                       (set-seek-mode false))

                                     (keycodes/play-broad-sense? keyCode)
                                     (do
                                       (utils/stop-event-propagation %)
                                       (do-seek! video-ref.current @new-seek)
                                       (.play video-ref.current)
                                       (set-seek-mode false))

                                     :else nil)
                                   (.resume js/SpatialNavigation))}
             [continue-watching-bar
              {:width (get-dynamic-media-percent)
               :height (when @seek-mode? "h-2")
               :class "transition-[width] transition-[height] duration-200 easy-in-out"
               :clickable? true}]]
            [:div {:class "text-white font-bold text-2xl"}
             (video-duration media-duration)]]
           [:div {:class ["px-32 flex items-center justify-center space-x-4"
                          (when @seek-mode? "invisible")]}

            (when (and main-language
                       (not= main-language "pt-BR"))
              [track-button {:selected?   (and (= main-language current-audio)
                                               (= "pt-BR" current-subtitle))
                             :tab-index   tab-index
                             :label       (str (format-language main-language)
                                               " (original)"
                                               (when (> (count subtitle-tracks) 1)
                                                 " com legenda"))
                             :on-key-down #(let [event %
                                                 keyCode (-> event .-keyCode)]
                                             (when (keycodes/up? keyCode)
                                               (utils/stop-event-propagation %)
                                               (.focus play-pause-btn-ref.current)))
                             :on-click    #(do (utils/stop-event-propagation %)
                                               (when (and (not= main-language current-audio)
                                                          set-audio-track)
                                                 (set-audio-track main-audio))
                                               (when (and (not= "pt-BR" current-subtitle)
                                                          (> (count subtitle-tracks) 1)
                                                          set-subtitle-track)
                                                 (set-subtitle-track {:language "pt-BR"})))}])

            (when (some #(= "pt-BR" (:language %)) audio-tracks)
              [track-button {:selected?   (and (= "pt-BR" current-audio)
                                               (= "none" current-subtitle))
                             :tab-index   tab-index
                             :label       (str "Português "
                                               (if (= main-language "pt-BR")
                                                 "(original)"
                                                 "(dublado)"))
                             :on-key-down #(let [event %
                                                 keyCode (-> event .-keyCode)]
                                             (when (keycodes/up? keyCode)
                                               (utils/stop-event-propagation %)
                                               (.focus play-pause-btn-ref.current)))
                             :on-click    #(do (utils/stop-event-propagation %)
                                               (when (and (not= "pt-BR" current-audio)
                                                          set-audio-track)
                                                 (set-audio-track {:language "pt-BR"}))
                                               (when set-subtitle-track
                                                 (set-subtitle-track {:language "none"})))}])

            (when show-others? [Button {:color         "black"
                                        :tab-index     tab-index
                                        :extra-classes ["outline-none"]
                                        :on-key-down   #(let [event %
                                                              keyCode (-> event .-keyCode)]
                                                          (cond
                                                            (keycodes/right? keyCode)
                                                            (utils/stop-event-propagation %)

                                                            (keycodes/ok? keyCode)
                                                            (do
                                                              (utils/stop-event-propagation %)
                                                              (when toggle-track-selector (toggle-track-selector))
                                                              (js/setTimeout (fn []
                                                                               (.focus js/SpatialNavigation "player-side"))
                                                                             utils/delay-to-focus))

                                                            (keycodes/up? keyCode)
                                                            (do
                                                              (utils/stop-event-propagation %)
                                                              (.focus play-pause-btn-ref.current))

                                                            :else nil))
                                        :on-click      #(do
                                                          (utils/stop-event-propagation %)
                                                          (when toggle-track-selector (toggle-track-selector))
                                                          (js/setTimeout (fn []
                                                                           (.focus js/SpatialNavigation "player-side"))
                                                                         utils/delay-to-focus))}
                                [:span {:class "px-5 py-1"} "Outros..."]])]]
          (when @show-suggestion?
            [:div {:class "watch-next space-y-4"}
             [:div {:class "h-[30vh] overflow-y-scroll hide-scroll-bar last:pb-28"}
              [:div {:class "flex flex-col"}
               [:div {:class "text-4xl text-white pl-2"} "Aulas"]
               [carousel {:thumbs (mapv thumb-gen ["playlists/programa/heroWeb/0bb66360-5f0d-11ee-ad9f-7343159856b0"
                                                   "playlists/programa/heroWeb/0bb66360-5f0d-11ee-ad9f-7343159856b0"
                                                   "playlists/programa/heroWeb/0bb66360-5f0d-11ee-ad9f-7343159856b0"
                                                   "playlists/programa/heroWeb/0bb66360-5f0d-11ee-ad9f-7343159856b0"
                                                   "playlists/programa/heroWeb/0bb66360-5f0d-11ee-ad9f-7343159856b0"
                                                   "playlists/programa/heroWeb/0bb66360-5f0d-11ee-ad9f-7343159856b0"
                                                   "playlists/programa/heroWeb/0bb66360-5f0d-11ee-ad9f-7343159856b0"])}]]
              [:div {:class "flex flex-col"}
               [:div {:class "text-4xl text-white pl-2"} "Relacionados"]
               [carousel {:thumbs (mapv thumb-gen ["playlists/programa/sixteenNine/a475e060-5d66-11ee-9a69-ab5b937322bf"
                                                   "playlists/medias/sixteenNine/2a5366e0-59f2-11ed-943a-8b1bc082acb8"
                                                   "playlists/programa/heroWeb/0bb66360-5f0d-11ee-ad9f-7343159856b0"
                                                   "playlists/programa/sixteenNine/35eab860-ffd7-11ed-8771-719d26fdb87a"
                                                   "playlists/programa/sixteenNine/61184290-5d2f-11ee-92f3-01dde0ecba2c"
                                                   "playlists/programa/heroWeb/0bb66360-5f0d-11ee-ad9f-7343159856b0"
                                                   "playlists/programa/heroWeb/0bb66360-5f0d-11ee-ad9f-7343159856b0"
                                                   "playlists/programa/heroWeb/0bb66360-5f0d-11ee-ad9f-7343159856b0"])}]]]])]]))))

(defn- setup-video-events
  [video-el handler]
  (events/listen video-el EventType/PLAYING handler)
  (events/listen video-el EventType/PLAY handler)
  (events/listen video-el EventType/PAUSE handler)
  (events/listen video-el EventType/SEEKED handler)
  (events/listen video-el EventType/SEEKING handler)
  (events/listen video-el EventType/WAITING handler)
  (events/listen video-el EventType/CANPLAY handler)
  (events/listen video-el EventType/TIMEUPDATE handler)
  (events/listen video-el EventType/ENDED handler)

  ;; monitoring events
  ;; (events/listen video-el EventType/STALLED handler)
  (events/listen video-el EventType/ERROR handler)
  ;; (events/listen video-el EventType/SUSPEND handler)
  (events/listen video-el EventType/ABORT handler))

(defn- get-media-info
  [shaka media]
  (let [audio-tracks      (.getAudioLanguagesAndRoles shaka)
        subtitle-tracks   (.getTextLanguagesAndRoles shaka)
        variants-tracks   (.getVariantTracks shaka)
        text-tracks       (.getTextTracks shaka)
        k                 #(js->clj % :keywordize-keys true)
        disabled-subtitle [{:language "none" :label "Desativado"}]]
    {:audio-tracks    (mapv k audio-tracks)
     :variants        (mapv k variants-tracks)
     :texts           (mapv k text-tracks)
     :subtitle-tracks (apply merge disabled-subtitle (mapv k subtitle-tracks))
     :media           media}))

(defn- rating
  [{:keys [media-sub config-sub] :as _props}]
  (let [media                  (rf/subscribe media-sub)
        config                 (rf/subscribe config-sub)
        {:keys [url advisory]} (-> @media :drm :rating)]
    (when url
      [:div {:class ["absolute top-20 left-32 z-10 transition duration-500"
                     (when-not (-> @config :show-rating?) "opacity-0")]}
       [:div {:class "flex flex-col pl-4 border-l-2 border-primary-500 text-white"}
        [:div {:class "flex text-center mr-3 place-items-center"}
         [:div {:class "text-4xl font-bold mr-3"} "Classificação Indicativa"]
         [:div {:class "pt-1"} [:img {:src url}]]]
        (when (-> advisory (count) (pos?))
          [:div {:class "pl-1 text-2xl"} (str/join ", " advisory)])]])))

(defn- shaka-set-audio
  [shaka cb {:keys [language]}]
  (when shaka
    (.selectAudioLanguage shaka language)
    (when cb (cb language))))

(defn- shaka-set-subtitle
  [shaka cb {:keys [language]}]
  (when shaka
    (cond
      (not= "none" language)
      (do (.setTextTrackVisibility shaka true)
          (.selectTextLanguage shaka language))

      (any-forced-subs? shaka)
      ;; selects a forced subtitle, if it exists
      (.selectTextLanguage shaka nil nil true)

      :else
      (.setTextTrackVisibility shaka false))
    (when cb (cb language))))

(defn loading-overlay
  [{:keys [config-sub]}]
  (let [config (rf/subscribe config-sub)]
    (fn []
      (let [player-loading? (-> @config :can-play? not)]
        (when player-loading? [:div {:class "absolute top-0 w-full h-full z-50 opacity-50 bg-blend-darken"} [loading]])))))

(defn- add-player-info-to-rollbar-context! []
  (-> shaka-player/Player
      (.probeSupport)
      (.then (fn [support-response]
               (logger/set-player-info! support-response)))))

(defn- get-webos-info [a]
  (when (= "webos" os-system/device)
    (.deviceInfo js/webOS (fn [device]
                            (reset! a (.-screenHeight device))))))

(defn debug-view
  [player]
  (let [stats-res       (r/atom nil)
        stats-bandwidth (r/atom nil)
        webos-info      (r/atom nil)]
    (r/create-class
     {:component-did-mount (fn [_]
                             (let [print-stats-fn #(let [stats (-> @player :shaka .getStats)]
                                                     (reset! stats-bandwidth (.-estimatedBandwidth stats))
                                                     (reset! stats-res (.-height stats)))]
                               (get-webos-info webos-info)
                               (js/setInterval print-stats-fn 2000)))
      :reagent-render      (fn []
                             [:div {:class ["z-30 absolute top-0 p-10"
                                            "bg-black/75"
                                            "rounded"
                                            "text-white text-2xl"]}
                              [:p "Versão atual: " logger/code-version]
                              [:p "Bandwidth estimada: " @stats-bandwidth]
                              [:p "Resolução atual: " @stats-res]
                              [:p "Resolução webos: " @webos-info]])})))

(defn player
  "Player component.
  Components here need to be really careful when updating DOM. Because SHAKA might loose its reference to the video element."

  [{:keys [class media-sub events config-sub user-sub tab-index token-sub toggle-track-selector]}]
  (let [{:keys [on-pause on-video-update set-media-info on-show-player-controls
                select-audio select-subtitle on-close-player]} events
        video-ref                              (createRef)
        media                                  (rf/subscribe media-sub)
        config                                 (rf/subscribe config-sub)
        token                                  (rf/subscribe token-sub)
        user                                   (rf/subscribe user-sub)
        initial-state                          {}
        fatal-error?                           (r/atom false)
        error-data                             (r/atom {})
        timer                                  (r/atom nil)
        state                                  (r/atom initial-state)
        clearTimer                             #(js/clearTimeout @timer)
        container-ref                          (createRef)]
    (fn []
      (r/create-class
       {:component-did-mount    (fn [_react-component]
                                  (let [player             (or (:shaka state)
                                                               (shaka-player/Player. video-ref.current))]

                                    (swap! state assoc :shaka player)
                                    (swap! state assoc :refs {:video     video-ref.current
                                                              :container (.-current container-ref)})
                                    (when (fn? on-video-update)
                                      (setup-video-events video-ref.current on-video-update))))
        :component-will-unmount (fn []
                                  (clearTimer)
                                  (.detach (:shaka @state)))
        :reagent-render         (fn [_props]
                                  (let [token @token]
                                    (when (and (-> @media :drm :manifest :url) (:shaka @state))
                                      (config-player (:shaka @state) (:refs @state)
                                                     {:media-id      (:id @media)
                                                      :token         token
                                                      :user          @user})
                                      (when-not @fatal-error?
                                        (load-and-play (:shaka @state)
                                                       (-> @media :drm :manifest :url)
                                                       fatal-error?
                                                       error-data
                                                       #(do (add-player-info-to-rollbar-context!)
                                                            (when set-media-info (-> @state :shaka (get-media-info @media) set-media-info))
                                                            (set-current-time (-> @state :refs :video)
                                                                              (-> @media :drm :checkpoint))
                                                            (when-let [audio-track-selected (-> @config
                                                                                                :controls
                                                                                                :audio-track-selected)]
                                                              (shaka-set-audio (:shaka @state)
                                                                               nil
                                                                               {:language audio-track-selected}))
                                                            (when-let [subtitle-track-selected (-> @config
                                                                                                   :controls
                                                                                                   :subtitle-track-selected)]
                                                              (shaka-set-subtitle (:shaka @state)
                                                                                  nil
                                                                                  {:language subtitle-track-selected}))))))

                                    [:div {:ref      container-ref
                                           :class    ["relative overflow-hidden" class]
                                           :on-click #(when on-pause
                                                        (on-pause (clj->js {:type "PLAY/PAUSE" :target video-ref.current})))}
                                     [loading-overlay {:config-sub config-sub}]
                                     (when @fatal-error?
                                       [player-fatal-error @error-data (+ tab-index 10)])
                                     [:f> controls {:media-sub  media-sub
                                                    :state      state
                                                    :tab-index  tab-index
                                                    :events     (merge events {:set-subtitle-track #(shaka-set-subtitle (:shaka @state) select-subtitle %)
                                                                               :set-audio-track    #(shaka-set-audio (:shaka @state) select-audio %)
                                                                               :on-close-player    on-close-player})
                                                    :video-ref  video-ref
                                                    :config-sub config-sub
                                                    :on-show-player-controls on-show-player-controls
                                                    :toggle-track-selector toggle-track-selector}]
                                     (when (contains? resolution-test-users (:email @user))
                                       [debug-view state])
                                     [:div {:class ["z-0 flex items-center justify-center h-full"]}
                                      [:video {:ref         video-ref
                                               :class       "video z-0 w-full h-full object-contain"
                                               :autoPlay    true
                                               :on-can-play #(do (if (-> @config :controls :paused? not)
                                                                   (.play (-> % .-target))
                                                                   (.pause (-> % .-target)))

                                                                 (logger/mux-setup {:video-el (.-target %)
                                                                                    :video-id (:id @media)
                                                                                    :video-title (-> @media :drm :name)
                                                                                    :video-duration (-> @media :drm :duration)
                                                                                    :viewer-user-id (-> @user :id)
                                                                                    :view-drm-type (-> @state :shaka .keySystem)
                                                                                    :playlist-id    (-> @media :drm :playlist :id)}))
                                               :poster      (cloudinary-image (-> @media :playlist :hero :image))}]]
                                     [rating {:media-sub  media-sub
                                              :config-sub config-sub}]]))}))))
